import { useTranslation } from '@rhino-project/core/i18n';
import {
  useSessions,
  useRevokeSession,
  useRevokeOtherSessions
} from '@rhino-project/core/queries';
import { Button, Card, CardContent, Chip } from '@heroui/react';

interface SessionRow {
  id: number;
  device_label: string;
  ip_address: string | null;
  last_active_at: string;
  current: boolean;
}

export const Sessions = () => {
  const { t, locale } = useTranslation();
  // Data is prefetched by the route loader (prefetchSessions), so this useQuery
  // hits a warm cache and renders without a loading spinner. The `data ?? []`
  // fallback keeps a non-prefetched mount rendering an empty state.
  const { data } = useSessions();
  const { mutate: revoke, isPending: revoking } = useRevokeSession();
  const { mutate: revokeOthers, isPending: revokingOthers } =
    useRevokeOtherSessions();

  const sessions = (data ?? []) as SessionRow[];
  const hasOthers = sessions.some((s) => !s.current);

  const handleRevoke = (id: number) => revoke(id);

  return (
    <div className="flex flex-col gap-4">
      {hasOthers && (
        <div className="flex justify-end">
          <Button
            variant="danger-soft"
            isDisabled={revokingOthers}
            onPress={() => revokeOthers()}
          >
            {t('rhino.frontend.messages.sign_out_all_other_devices')}
          </Button>
        </div>
      )}

      {sessions.map((session) => (
        <Card key={session.id}>
          <CardContent className="flex flex-row items-center justify-between">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <span className="font-medium">{session.device_label}</span>
                {session.current && (
                  <Chip size="sm" color="success" variant="soft">
                    {t('rhino.frontend.messages.this_device')}
                  </Chip>
                )}
              </div>
              <span className="text-sm text-muted">
                {session.ip_address ?? 'Unknown IP'} ·{' '}
                {new Date(session.last_active_at).toLocaleString(
                  locale.replaceAll('_', '-')
                )}
              </span>
            </div>

            {!session.current && (
              <Button
                size="sm"
                variant="danger-soft"
                isDisabled={revoking}
                onPress={() => handleRevoke(session.id)}
              >
                {t('rhino.frontend.messages.sign_out_action')}
              </Button>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};
